import { useState } from "react"
import { Head } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

interface Invoice {
  id: string
  label: string
  createdOn: string | null
  amountLabel: string
  hostedInvoiceUrl: string | null
}

interface Overdue {
  error: boolean
  count: number
  totalLabel: string
  suspensionOn: string | null
  oldestInvoiceOn: string | null
  invoices: Invoice[]
}

interface StoreOrder {
  id: number
  month: string
  amountLabel: string
  pdfPath: string | null
}

interface Section {
  title: string
  message: string | null
}

interface Translations {
  title: string
  subtitle: string
  error: Section
  suspended: Section
  deadline: Section
  upToDate: Section
  stats: {
    total: string
    count: string
    deadline: string
    noDeadline: string
  }
  since: string | null
  portal: { title: string; description: string; unavailable: string }
  invoices: {
    title: string
    description: string
    number: string
    date: string
    amount: string
    action: string
  }
  actions: { updateCard: string; pay: string; resubscribe: string }
  resubscribe: { title: string; description: string }
  tabs: { subscription: string; commissions: string }
  commissions: {
    title: string
    description: string
    month: string
    amount: string
    pdf: string
    empty: string
    emptyHint: string
  }
}

interface ShowProps {
  overdue: Overdue
  suspended: boolean
  billingPortalAvailable: boolean
  portalPath: string
  canResubscribe: boolean
  subscribePath: string
  storeOrders: StoreOrder[]
  t: Translations
}

type AlertTone = "error" | "warning" | "success"

const ALERT_TONES: Record<AlertTone, string> = {
  error: "border-red-500 bg-red-50 text-red-900",
  warning: "border-orange-500 bg-orange-50 text-orange-900",
  success: "border-green-500 bg-green-50 text-green-900",
}

function Alert({ tone, title, message }: { tone: AlertTone; title: string; message: string | null }) {
  return (
    <div className={`rounded-r border-l-4 p-4 ${ALERT_TONES[tone]}`} role="status">
      <h2 className="text-base font-bold">{title}</h2>
      {message && <p className="mt-1 text-sm">{message}</p>}
    </div>
  )
}

function Stat({ label, value, sublabel }: { label: string; value: string; sublabel?: string | null }) {
  return (
    <div className="rounded-lg border border-contour bg-white p-4">
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-content">{value}</p>
      {sublabel && <p className="mt-1 text-xs text-gray-500">{sublabel}</p>}
    </div>
  )
}

/**
 * Which alert heads the page. The error state wins over everything: when Stripe
 * could not be reached we know nothing about what is owed, so promising a
 * deadline — or an all-clear — would be a lie.
 */
function headlineAlert(overdue: Overdue, suspended: boolean, t: Translations) {
  if (overdue.error) return { tone: "error" as const, ...t.error }
  if (overdue.count === 0) return { tone: "success" as const, ...t.upToDate }
  if (suspended) return { tone: "error" as const, ...t.suspended }
  if (overdue.suspensionOn) return { tone: "warning" as const, ...t.deadline }
  return null
}

function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
}

export default function BillingShow({
  overdue,
  suspended,
  billingPortalAvailable,
  portalPath,
  canResubscribe,
  subscribePath,
  storeOrders,
  t,
}: ShowProps) {
  const alert = headlineAlert(overdue, suspended, t)
  // L'onglet Abonnement s'ouvre en premier : c'est lui qui porte une dette
  // éventuelle, donc l'information qui demande une action.
  const [tab, setTab] = useState<"subscription" | "commissions">("subscription")

  return (
    <>
      <Head title={t.title} />
      <main className="mx-auto max-w-5xl px-4 py-8 md:px-8">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-content">{t.title}</h1>
          <p className="mt-1 text-sm text-gray-500">{t.subtitle}</p>
        </header>

        <div className="mb-6 flex gap-1 border-b border-contour" role="tablist">
          {(["subscription", "commissions"] as const).map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold transition-colors ${
                tab === key
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 hover:text-content"
              }`}
            >
              {t.tabs[key]}
            </button>
          ))}
        </div>

        {tab === "subscription" && (
          <>
        {alert && <Alert tone={alert.tone} title={alert.title} message={alert.message} />}

        {!overdue.error && (
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            <Stat label={t.stats.total} value={overdue.totalLabel} />
            <Stat label={t.stats.count} value={String(overdue.count)} />
            <Stat
              label={t.stats.deadline}
              value={overdue.suspensionOn ?? t.stats.noDeadline}
              sublabel={t.since}
            />
          </div>
        )}

        {overdue.invoices.length > 0 && (
          <section className="mt-6 rounded-lg border border-contour bg-white p-6">
            <h2 className="text-lg font-semibold text-content">{t.invoices.title}</h2>
            <p className="mt-1 text-sm text-gray-500">{t.invoices.description}</p>
            <div className="mt-4 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t.invoices.number}</TableHead>
                    <TableHead>{t.invoices.date}</TableHead>
                    <TableHead>{t.invoices.amount}</TableHead>
                    <TableHead>{t.invoices.action}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overdue.invoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-medium">{invoice.label}</TableCell>
                      <TableCell>{invoice.createdOn ?? "—"}</TableCell>
                      <TableCell>{invoice.amountLabel}</TableCell>
                      <TableCell>
                        {invoice.hostedInvoiceUrl && (
                          <a
                            href={invoice.hostedInvoiceUrl}
                            target="_blank"
                            rel="noopener"
                            className="font-semibold text-primary underline"
                          >
                            {t.actions.pay}
                          </a>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>
        )}

        <section className="mt-6 rounded-lg border border-contour bg-white p-6">
          <h2 className="text-lg font-semibold text-content">{t.portal.title}</h2>
          <p className="mt-1 text-sm text-gray-500">{t.portal.description}</p>
          <div className="mt-4">
            {billingPortalAvailable ? (
              // Full page load, not an Inertia visit: the portal lives on Stripe's domain.
              <form action={portalPath} method="post" data-turbo="false">
                <input type="hidden" name="_method" value="delete" />
                <input type="hidden" name="authenticity_token" value={csrfToken()} />
                <Button type="submit">{t.actions.updateCard}</Button>
              </form>
            ) : (
              <p className="text-sm text-gray-500">{t.portal.unavailable}</p>
            )}
          </div>
        </section>

        {canResubscribe && (
          <section className="mt-6 rounded-lg border border-contour bg-white p-6">
            <h2 className="text-lg font-semibold text-content">{t.resubscribe.title}</h2>
            <p className="mt-1 text-sm text-gray-500">{t.resubscribe.description}</p>
            <form action={subscribePath} method="post" data-turbo="false" className="mt-4">
              <input type="hidden" name="authenticity_token" value={csrfToken()} />
              <Button type="submit">{t.actions.resubscribe}</Button>
            </form>
          </section>
        )}
          </>
        )}

        {tab === "commissions" && (
          <section className="rounded-lg border border-contour bg-white p-6">
            <h2 className="text-lg font-semibold text-content">{t.commissions.title}</h2>
            <p className="mt-1 text-sm text-gray-500">{t.commissions.description}</p>
            {storeOrders.length === 0 ? (
              <div className="mt-6 rounded-lg border border-dashed border-gray-300 p-10 text-center">
                <p className="text-sm font-medium text-content">{t.commissions.empty}</p>
                <p className="mt-1 text-sm text-gray-500">{t.commissions.emptyHint}</p>
              </div>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t.commissions.month}</TableHead>
                      <TableHead>{t.commissions.amount}</TableHead>
                      <TableHead>{t.commissions.pdf}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {storeOrders.map((so) => (
                      <TableRow key={so.id}>
                        <TableCell className="font-medium">{so.month}</TableCell>
                        <TableCell>{so.amountLabel}</TableCell>
                        <TableCell>
                          {so.pdfPath && (
                            <a href={so.pdfPath} className="text-primary underline" data-turbo="false">
                              <i className="fa-solid fa-file-pdf text-red-500" aria-hidden="true" />
                              <span className="sr-only">{t.commissions.pdf}</span>
                            </a>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </section>
        )}

      </main>
    </>
  )
}
